import { type AbstractEngine, Scene } from "@babylonjs/core";
import { create } from "@bufbuild/protobuf";
import { LocalPlayer } from "../engine/player";
import { type ClientMessage, EnterRoomSchema, LeaveRoomSchema } from "../gen/livesoup/v1/realtime_pb";
import type { AssetInfo } from "../net/api";
import { client } from "../net/codec";
import { $connection, $myAssets, $panel, toast } from "../state/stores";
import { Inspector } from "./inspector";
import { $inspector } from "./inspectorState";
import { ROOM_SPAWN } from "./layout";
import { type Look, actionFor, promptFor, sameLook } from "./look";
import type { ModelLoader } from "./models";
import { $roomAction, $roomPhase, $roomRequest } from "./phase";
import { ShelfView } from "./shelfView";
import { type BuiltRoom, buildRoom, ROOM_SPACE } from "./template";
import { $touchMode } from "../state/touch";

const PICK_MS = 100;
const REACH = 4; // metres the crosshair reaches

export interface RoomSession {
  ws: { send(m: ClientMessage): void };
  player: { setEnabled(on: boolean): void; setMovementBlocked(b: boolean): void };
  whenWelcomed(cb: () => void): void;
}

export interface RoomDeps {
  engine: AbstractEngine;
  lobby: Scene;
  show(s: Scene): void;
  canvas: HTMLCanvasElement;
  session: RoomSession;
  hud: { setPrompt(text: string | null): void };
  fade(dark: boolean): Promise<void>;
  load?: ModelLoader;
}

interface Built { scene: Scene; room: BuiltRoom; player: LocalPlayer; shelf: ShelfView; inspector: Inspector }

/** Going to the room and back, the shelf and the inspector (spec 2o §2, §4.1). */
export class RoomController {
  private built: Built | null = null;
  private pendingLeave = false;
  private started = false; // been in the room once, so the page's start no longer applies
  private look: Look = null;
  private readonly stops: (() => void)[] = [];

  constructor(private readonly d: RoomDeps) {
    this.stops.push($roomRequest.listen(() => void this.toggle()));
    this.stops.push($panel.subscribe((p) => { if (p !== "inspector" && this.built?.inspector.isOpen()) this.closeInspector(); }));
    this.stops.push($myAssets.subscribe((list) => this.assetsChanged(list)));
    // the prompt's wording follows the hands: a finger or a key mid-look rewords it at once
    this.stops.push($touchMode.listen(() => { if (this.look) this.setLook(this.look); }));
    const timer = setInterval(() => this.pick(), PICK_MS);
    this.stops.push(() => clearInterval(timer));
    d.session.whenWelcomed(() => this.welcomed());
  }

  dispose(): void { for (const stop of this.stops.splice(0)) stop(); }

  active(): boolean { return $roomPhase.get() !== "lobby"; }

  /** The player the keys and panels block: the room's while in the room. */
  activePlayer(): { setMovementBlocked(b: boolean): void } {
    return this.active() && this.built ? this.built.player : this.d.session.player;
  }

  async toggle(): Promise<void> {
    const phase = $roomPhase.get();
    if (phase === "lobby") await this.enter();
    else if (phase === "room") await this.leave();
  }

  /** E in the room: turn the page, or inspect the model under the crosshair. */
  interact(): void {
    const look = this.look;
    if ($roomPhase.get() !== "room" || !look) return;
    if (look.kind === "sign") { this.built?.shelf.turn(look.dir); return; }
    const asset = $myAssets.get().find((a) => a.code === look.code);
    if (asset) this.inspect(asset);
  }

  inspect(asset: AssetInfo): void {
    const b = this.built;
    if (!b || $roomPhase.get() !== "room") return;
    b.shelf.hover(null);
    this.setLook(null);
    b.player.setEnabled(false);
    $panel.set("inspector");
    void b.inspector.open(asset);
  }

  private build(): Built {
    if (this.built) return this.built;
    const scene = new Scene(this.d.engine);
    const room = buildRoom(scene);
    const player = new LocalPlayer(scene, this.d.canvas, ROOM_SPACE);
    this.built = { scene, room, player, shelf: new ShelfView(scene, room, this.d.load), inspector: new Inspector(scene, this.d.canvas, room.turntableTop, this.d.load) };
    return this.built;
  }

  private send(m: ClientMessage): void { if ($connection.get() === "online") this.d.session.ws.send(m); }

  /**
   * A fresh page starts in the room, once (spec 2o §2.1). Offline the EnterRoom waits for the welcome, as after
   * a reconnect; the server keeps the lobby spawn as the place R goes back to.
   */
  startInRoom(): void {
    if (this.started || $roomPhase.get() !== "lobby") return;
    this.arrive();
  }

  private async enter(): Promise<void> {
    $roomPhase.set("entering");
    await this.d.fade(true);
    this.arrive();
    await this.d.fade(false);
  }

  private arrive(): void {
    this.started = true;
    const b = this.build();
    this.send(client({ case: "enterRoom", value: create(EnterRoomSchema) }));
    this.d.session.player.setEnabled(false);
    b.shelf.setAssets($myAssets.get());
    b.player.setEnabled(true);
    b.player.spawn({ x: ROOM_SPAWN.x, y: 0, z: ROOM_SPAWN.z }, ROOM_SPAWN.yaw);
    this.d.show(b.scene);
    $roomPhase.set("room");
  }

  private async leave(): Promise<void> {
    if ($connection.get() !== "online") { // spec 2o §5: waits for the reconnect
      this.pendingLeave = true;
      toast("連線中，重新連上後回到大廳");
      return;
    }
    await this.backToLobby(true);
  }

  private async backToLobby(send: boolean): Promise<void> {
    const b = this.built!;
    if ($panel.get() === "inspector") $panel.set(null);
    $roomPhase.set("leaving");
    await this.d.fade(true);
    if (send) this.send(client({ case: "leaveRoom", value: create(LeaveRoomSchema) }));
    b.shelf.hover(null);
    this.setLook(null);
    b.player.setEnabled(false);
    this.d.session.player.setEnabled(true);
    this.d.show(this.d.lobby);
    $roomPhase.set("lobby");
    await this.d.fade(false);
  }

  /** A new connection starts in the lobby at the spawn (spec 2o §3.7): go back in, or finish a queued leave. */
  private welcomed(): void {
    if ($roomPhase.get() !== "room") return;
    if (this.pendingLeave) { this.pendingLeave = false; void this.backToLobby(false); return; }
    this.send(client({ case: "enterRoom", value: create(EnterRoomSchema) }));
  }

  private closeInspector(): void {
    const b = this.built!;
    b.inspector.close();
    if ($roomPhase.get() === "room") b.player.setEnabled(true);
  }

  private assetsChanged(list: readonly AssetInfo[]): void {
    const b = this.built;
    if (!b || !this.active()) return;
    b.shelf.setAssets(list);
    const v = $inspector.get();
    if (v && !list.some((a) => a.code === v.code)) { // spec 2o §5
      $panel.set(null);
      toast("這個模型已不在你的架上");
    }
  }

  private pick(): void {
    const b = this.built;
    if ($roomPhase.get() !== "room" || !b || $panel.get()) return;
    const hit = b.scene.pickWithRay(b.player.camera.getForwardRay(REACH), (m) => m.isPickable && m.isEnabled());
    const mesh = hit?.pickedMesh ?? null;
    const code = b.shelf.codeAt(mesh);
    const sign = (mesh?.metadata as { roomSign?: -1 | 1 } | null)?.roomSign;
    const look: Look = code ? { kind: "slot", code } : sign ? { kind: "sign", dir: sign } : null;
    if (sameLook(look, this.look)) return;
    b.shelf.hover(look?.kind === "slot" ? look.code : null);
    this.setLook(look);
  }

  private setLook(look: Look): void {
    this.look = look;
    const failed = (c: string) => this.built?.shelf.failed(c) ?? false;
    this.d.hud.setPrompt(promptFor(look, $myAssets.get(), failed));
    $roomAction.set(actionFor(look, failed));
  }
}
